// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import { purchaseOrdersQueryKey } from '@/purchase-order/query-keys';
import { returnOrdersQueryKey } from '@/return-order/query-keys';
import {
  ResolveQuarantinedSerialRequest,
  resolveQuarantinedSerial,
} from '@/shipment/api/resolve-quarantined-serial';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface ResolveVariables extends ResolveQuarantinedSerialRequest {
  quarantineKey: string;
}

/**
 * React hook for resolving a held serial on a shipment.
 *
 * A resolution moves an asset and re-syncs the shipment's parent order, so it
 * invalidates the shipment, every order family (a hold can belong to an FO, PO,
 * or RO), assets, locations, and inventory.
 *
 * @param shipmentKey - The key of the shipment holding the serials
 * @returns Mutation object for resolving a held serial
 */
export function useResolveQuarantinedSerial(shipmentKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: ResolveVariables) =>
      resolveQuarantinedSerial(shipmentKey, variables.quarantineKey, {
        kind: variables.kind,
        note: variables.note,
        correctedSerial: variables.correctedSerial,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(shipmentKey),
      });
      void queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      void queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      void queryClient.invalidateQueries({ queryKey: purchaseOrdersQueryKey });
      void queryClient.invalidateQueries({ queryKey: returnOrdersQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['asset'] });
      void queryClient.invalidateQueries({ queryKey: ['location'] });
      invalidateInventory(queryClient);
    },
    // A refusal usually means the hold moved on since the panel loaded.
    onError: () => {
      void queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(shipmentKey),
        // Mark stale without refetching under the open dialog.
        refetchType: 'none',
      });
    },
  });
}
